import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  EuiPanel,
  EuiTitle,
  EuiSpacer,
  EuiText,
  EuiBadge,
  EuiButton,
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiHorizontalRule,
  EuiCallOut,
  EuiEmptyPrompt,
  EuiInMemoryTable,
  EuiBasicTableColumn,
  EuiStat,
} from "@elastic/eui";
import { OpsanioApplianceData } from "..";
import { OpsanioKeycloakLoginStats, OpsanioKeycloakUserLogin } from "src/questionnaires";
import { useApi } from "src/api";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { FormSkeleton } from "src/components/skeleton";

const i18n = "anketa.opsanio.appliance.flyout.auth";

const formatLastLogin = (ms: number | null): string => {
  if (!ms) return "-";
  const d = new Date(ms);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleString("de-DE", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const OpsanioApplianceFlyoutAuth = ({
  appliance,
}: {
  appliance: OpsanioApplianceData;
}): JSX.Element => {
  const { Get } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();

  const instanceName = appliance.common?.metadata?.name?.trim() ?? "";
  const [stats, setStats] = useState<OpsanioKeycloakLoginStats | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [unavailable, setUnavailable] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const canFetch = useMemo<boolean>(
    () => typeof keycloak?.token === "string" && instanceName !== "",
    [keycloak, instanceName],
  );

  const getStats = useCallback(async () => {
    if (!canFetch) return;
    setLoading(true);
    setUnavailable(false);
    setLoadError(null);
    try {
      const { success, data, error } = await Get<any>({
        path: `v2/${AnketaResourcesEnum.opsanio}/keycloak/auths/${encodeURIComponent(
          instanceName,
        )}`,
        responseType: "json",
        token: keycloak.token,
      });
      if (!success || error) {
        if ((error as any)?.status === 503) {
          setUnavailable(true);
        } else {
          setLoadError(
            (error as any)?.message ??
              translate(`${i18n}.error.body`, {
                defaultMessage: "Login statistics could not be loaded.",
              }),
          );
        }
        setStats(null);
      } else {
        setStats(((data as any)?.data as OpsanioKeycloakLoginStats) ?? null);
      }
    } catch {
      setLoadError(
        translate(`${i18n}.error.body`, {
          defaultMessage: "Login statistics could not be loaded.",
        }),
      );
      setStats(null);
    } finally {
      setLoading(false);
    }
    // `Get` is excluded on purpose: useApi() returns a new Get each render, so
    // including it would re-run this effect every render (infinite loop).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canFetch, instanceName, keycloak, translate]);

  useEffect(() => {
    getStats();
  }, [getStats]);

  const columns: Array<EuiBasicTableColumn<OpsanioKeycloakUserLogin>> = [
    {
      field: "username",
      name: translate(`${i18n}.column.user`, { defaultMessage: "User" }),
      sortable: true,
      render: (username: string) => (
        <EuiFlexGroup gutterSize="xs" alignItems="center" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiIcon type="user" size="s" color="subdued" />
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="s">{username}</EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      ),
    },
    {
      field: "count",
      name: translate(`${i18n}.column.logins`, { defaultMessage: "Logins" }),
      sortable: true,
      width: "110px",
      render: (count: number) => <EuiBadge color="hollow">{count}</EuiBadge>,
    },
    {
      field: "lastLogin",
      name: translate(`${i18n}.column.lastLogin`, {
        defaultMessage: "Last login",
      }),
      sortable: true,
      width: "190px",
      render: (lastLogin: number | null) => (
        <EuiText size="xs" color="subdued">
          {formatLastLogin(lastLogin)}
        </EuiText>
      ),
    },
  ];

  const header = (
    <EuiFlexGroup
      justifyContent="spaceBetween"
      alignItems="center"
      gutterSize="s"
      responsive={false}
    >
      <EuiFlexItem>
        <EuiFlexGroup alignItems="center" gutterSize="s" responsive={false}>
          <EuiFlexItem grow={false}>
            <EuiIcon type="lock" size="l" />
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiTitle size="s">
              <h3 style={{ margin: 0 }}>
                {translate(`${i18n}.title`, {
                  defaultMessage: "SSO logins",
                })}
              </h3>
            </EuiTitle>
          </EuiFlexItem>
          {stats?.clientFound && (
            <EuiFlexItem grow={false}>
              <EuiBadge color="hollow">{stats.client}</EuiBadge>
            </EuiFlexItem>
          )}
        </EuiFlexGroup>
      </EuiFlexItem>
      <EuiFlexItem grow={false}>
        <EuiButton
          size="s"
          iconType="refresh"
          onClick={getStats}
          isLoading={loading}
          disabled={!canFetch}
        >
          {translate("anketa.opsanio.refresh", { defaultMessage: "Refresh" })}
        </EuiButton>
      </EuiFlexItem>
    </EuiFlexGroup>
  );

  const wrap = (body: React.ReactNode) => (
    <EuiPanel paddingSize="m" hasShadow={false} color="subdued">
      {header}
      <EuiHorizontalRule margin="m" />
      {body}
    </EuiPanel>
  );

  if (instanceName === "") {
    return wrap(
      <EuiCallOut
        color="warning"
        iconType="help"
        title={translate(`${i18n}.noInstance.title`, {
          defaultMessage: "No client identifier",
        })}
      >
        <p>
          {translate(`${i18n}.noInstance.body`, {
            defaultMessage:
              "This appliance has no VPC name (common.metadata.name), so no Keycloak client can be resolved.",
          })}
        </p>
      </EuiCallOut>,
    );
  }

  if (loading) return wrap(<FormSkeleton />);

  if (unavailable) {
    return wrap(
      <EuiCallOut
        color="warning"
        iconType="iInCircle"
        title={translate(`${i18n}.unavailable.title`, {
          defaultMessage: "Keycloak not configured",
        })}
      >
        <p>
          {translate(`${i18n}.unavailable.body`, {
            defaultMessage:
              "The medocino Keycloak connection is not configured on the server.",
          })}
        </p>
      </EuiCallOut>,
    );
  }

  if (loadError) {
    return wrap(
      <EuiCallOut
        color="danger"
        iconType="alert"
        title={translate(`${i18n}.error.title`, {
          defaultMessage: "Login statistics unavailable",
        })}
      >
        <p>{loadError}</p>
        <EuiButton size="s" color="danger" iconType="refresh" onClick={getStats}>
          {translate("anketa.common.retry", { defaultMessage: "Retry" })}
        </EuiButton>
      </EuiCallOut>,
    );
  }

  if (stats && !stats.clientFound) {
    return wrap(
      <EuiEmptyPrompt
        iconType="lock"
        title={
          <h3>
            {translate(`${i18n}.noClient.title`, {
              defaultMessage: "No SSO client found",
            })}
          </h3>
        }
        body={
          <EuiText size="s" color="subdued">
            {translate(`${i18n}.noClient.body`, {
              values: { client: stats.client, realm: stats.realm },
              defaultMessage:
                "No Keycloak client \"{{client}}\" exists in realm \"{{realm}}\" yet.",
            })}
          </EuiText>
        }
      />,
    );
  }

  if (stats && stats.users.length === 0) {
    return wrap(
      <EuiEmptyPrompt
        iconType="user"
        title={
          <h3>
            {translate(`${i18n}.empty.title`, {
              defaultMessage: "No logins recorded",
            })}
          </h3>
        }
        body={
          <EuiText size="s" color="subdued">
            {translate(`${i18n}.empty.body`, {
              defaultMessage:
                "No LOGIN events were found for this client in the audited window.",
            })}
          </EuiText>
        }
      />,
    );
  }

  return wrap(
    <>
      <EuiFlexGroup gutterSize="l" wrap>
        <EuiFlexItem>
          <EuiStat
            title={`${stats?.totalLogins ?? 0}`}
            description={translate(`${i18n}.stat.totalLogins`, {
              defaultMessage: "Total logins",
            })}
            titleSize="m"
          />
        </EuiFlexItem>
        <EuiFlexItem>
          <EuiStat
            title={`${stats?.uniqueUsers ?? 0}`}
            description={translate(`${i18n}.stat.uniqueUsers`, {
              defaultMessage: "Unique users",
            })}
            titleSize="m"
          />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="m" />
      <EuiInMemoryTable<OpsanioKeycloakUserLogin>
        items={stats?.users ?? []}
        columns={columns}
        sorting={{ sort: { field: "count", direction: "desc" } }}
        pagination={{ initialPageSize: 10, pageSizeOptions: [10, 25, 50] }}
        rowHeader="username"
      />
    </>,
  );
};

export default OpsanioApplianceFlyoutAuth;
